import { setAccessToken } from "./authTokens";
import { triggerLogout } from "../auth/authEvents";

let refreshPromise: Promise<string | null> | null = null;

/**
 * Centralized refresh token function with "promise pooling" (locking).
 * Prevents multiple simultaneous network requests for the same refresh event.
 * All concurrent callers will wait for the same promise to resolve.
 *
 * @async
 * @function performTokenRefresh
 * @returns {Promise<string | null>} The new access token, or null if refresh failed.
 */
export const performTokenRefresh = async (): Promise<string | null> => {
  // 🔒 If a refresh is already in flight, return the same promise
  if (refreshPromise) {
    return refreshPromise;
  }

  // 🚪 Open a new refresh request
  refreshPromise = (async () => {
    try {
      const response = await fetch("/api/auth/token/refresh", {
        method: "POST",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("REFRESH_FAILED");
      }

      const data = await response.json();
      const newAccessToken = data.accessToken || null;

      if (newAccessToken) {
        await setAccessToken(newAccessToken);
      }

      return newAccessToken;
    } catch (error) {
      console.error("[REFRESH] Error during token rotation:", error);
      await setAccessToken(null); // Clear local token
      triggerLogout(); // Force logout
      return null;
    } finally {
      // 🔓 Always clear the lock so subsequent expirations can trigger a new refresh
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};
